// crm-web/frontend/src/components/analytics/AnalyticsView.jsx
// Container principal con auto-refresh + wiring del deep-link cross-tab.

import { Suspense, useCallback, useEffect, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import useAnalyticsRange from '../../hooks/useAnalyticsRange'
import useAnalyticsTab from '../../hooks/useAnalyticsTab'
import useAnalyticsRefresh from '../../hooks/useAnalyticsRefresh'
import useVersionCheck from '../../hooks/useVersionCheck'
import useAnalyticsStore from '../../store/useAnalyticsStore'
import RangeSelector from './RangeSelector'
import RefreshControl from './RefreshControl'
import HeroCards from './HeroCards'
import TabNav from './TabNav'
import { TABS } from './tabRegistry'

function AdminFilterToggle() {
  const excludeAdminAgents = useAnalyticsStore(s => s.excludeAdminAgents)
  const setExcludeAdminAgents = useAnalyticsStore(s => s.setExcludeAdminAgents)
  return (
    <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={!excludeAdminAgents}
        onChange={(e) => setExcludeAdminAgents(!e.target.checked)}
        className="w-4 h-4 accent-primary-500 cursor-pointer"
      />
      <span>Incluir agentes administrativos</span>
    </label>
  )
}

function TabFallback() {
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-12 text-center">
      <div className="animate-pulse text-gray-400 dark:text-gray-500">Cargando...</div>
    </div>
  )
}

export default function AnalyticsView() {
  const { user } = useAuth()
  const { range, setRange } = useAnalyticsRange()
  const { tabId, setTabId } = useAnalyticsTab()

  // Deep-link: si otro componente dispara goToClassificationForAgent,
  // pendingTabId se setea y aqui lo aplicamos.
  const pendingTabId = useAnalyticsStore(s => s.pendingTabId)
  const acknowledgePendingTab = useAnalyticsStore(s => s.acknowledgePendingTab)
  useEffect(() => {
    if (pendingTabId && pendingTabId !== tabId) {
      setTabId(pendingTabId)
    }
    if (pendingTabId) {
      acknowledgePendingTab()
    }
  }, [pendingTabId, tabId, setTabId, acknowledgePendingTab])

  // Refresh: bump refreshKey -> tabs y HeroCards re-fetchean via useEffect deps
  const [refreshKey, setRefreshKey] = useState(0)
  const [lastRefresh, setLastRefresh] = useState(Date.now())

  const refresh = useCallback(() => {
    setRefreshKey(k => k + 1)
    setLastRefresh(Date.now())
  }, [])

  useAnalyticsRefresh(refresh)

  // Version check (corre cada 60s) - complementa al banner del Dashboard.
  const { hasUpdate, reload } = useVersionCheck()

  const activeTab = TABS.find(t => t.id === tabId) || TABS[0]
  const ActiveTabComponent = activeTab.component

  return (
    <div className="flex-1 flex flex-col p-6 bg-gray-50 dark:bg-surface-dark overflow-y-auto">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Analíticas
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {user?.is_admin ? 'Vista admin — todos los agentes' : 'Vista personal — solo tu data'}
          </p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <AdminFilterToggle />
          <RefreshControl lastRefresh={lastRefresh} onRefresh={refresh} hasUpdate={hasUpdate} onReload={reload} />
          <RangeSelector range={range} onChange={setRange} />
        </div>
      </div>

      <HeroCards range={range} refreshKey={refreshKey} />

      <TabNav tabs={TABS} activeId={tabId} onChange={setTabId} />

      <Suspense fallback={<TabFallback />}>
        <ActiveTabComponent range={range} refreshKey={refreshKey} />
      </Suspense>
    </div>
  )
}
